<template>
  <div style="margin:20px">

    <div class="actuals-page-header-banner">
      <div class="actuals-page-header-row">
        <span class="actuals-page-header-title">Actuals Troubleshooter</span>
        <span class="actuals-page-header-actions">
          <b-button size="sm" variant="primary" :to="back_link" style="margin-left: 8px;">
            <i class="fa fa-arrow-left" style="margin-right: 4px;"></i> <b>Back to Actuals</b>
          </b-button>
        </span>
      </div>
    </div>

    <div style="font-size: 22px; font-weight: bold; position: relative; margin: 20px" class="d-flex align-items-center justify-content-center">
      <b-button size="sm" variant="outline-secondary" title="Previous day" v-on:click.prevent="shift_dates(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"></date-picker>
      &nbsp;to&nbsp;
      <date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"></date-picker>&nbsp;
      <b-button size="sm" variant="outline-secondary" title="Next day" v-on:click.prevent="shift_dates(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_data()" :disabled="loading">Get Results</b-button>
    </div>

    <div v-if="loading">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="summary.totals">
      <div class="ts-summary-bar">
        <span class="ts-chip ts-chip-total">{{ summary.totals.movements }} {{ count_label }}</span>
        <span class="ts-chip ts-chip-success">Success {{ summary.totals.success }}</span>
        <span class="ts-chip ts-chip-error">Errors {{ summary.totals.error }}</span>
        <span class="ts-chip ts-chip-neutral">Unprocessed {{ summary.totals.unprocessed }}</span>
        <span class="ts-chip ts-chip-neutral">Cancelled {{ summary.totals.cancelled }}</span>
        <span class="ts-chip ts-chip-neutral">Skipped {{ summary.totals.skipped || 0 }}</span>
      </div>

      <div v-if="error_categories.length === 0" style="font-size: 20px; margin: 40px; text-align: center;">
        <i class="fa fa-check-circle" style="color: green; margin-right: 6px;"></i>
        No actuals errors found between {{ summary.start_date }} and {{ summary.end_date }}.
      </div>

      <div v-for="group in groups_with_categories" :key="group.id" class="ts-group">
        <div class="ts-group-header">
          {{ group.label }}
          <span class="ts-group-count">{{ group.bookings }} of {{ summary.totals.error }} errors</span>
        </div>

        <div v-for="cat in group.categories" :key="cat.id" class="ts-category-card">
          <div class="ts-category-header" @click="toggle_category(cat.id)">
            <span class="ts-category-toggle"><i class="fa" :class="expanded[cat.id] ? 'fa-minus-square-o' : 'fa-plus-square-o'"></i></span>
            <span class="ts-category-title">{{ cat.category }}</span>
            <span class="ts-category-stats">
              {{ cat.occurrences }} ({{ cat.pct }}% of errors)
            </span>
          </div>
          <div class="ts-category-body">
            <div class="ts-category-explanation"><b>What it means:</b> {{ cat.explanation }}</div>
            <div class="ts-category-fix"><b>Suggested fix:</b> {{ cat.suggested_fix }}</div>

            <div v-if="expanded[cat.id]" class="ts-category-bookings">
              <table class="ts-table">
                <tr>
                  <th>Booking</th>
                  <th>Vehicle</th>
                  <th v-if="group.id === 'telematics_matching'">Driver</th>
                  <th>Planned Start</th>
                  <th>Planned Finish</th>
                  <th>Errors</th>
                </tr>
                <tr v-for="booking in cat.bookings" :key="booking.booking_hash + cat.id">
                  <td>
                    <router-link :to="booking_link(booking)">{{ booking_label(booking) }}</router-link>
                  </td>
                  <td>{{ booking.vehicle }}</td>
                  <td v-if="group.id === 'telematics_matching'">{{ booking.driver_name || '' }}</td>
                  <td>{{ booking.planned_start | formatDateTimeSmall }}</td>
                  <td>{{ booking.planned_finish | formatDateTimeSmall }}</td>
                  <td>{{ (booking.errors || []).join(', ') }}</td>
                </tr>
              </table>
              <div v-if="cat.occurrences > cat.bookings.length" class="ts-more-note">
                showing first {{ cat.bookings.length }} of {{ cat.occurrences }} affected &mdash; use the Actuals page error filter to see all
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="unmatched_driver_names.length > 0" class="ts-group">
        <div class="ts-group-header">Driver names not found in telematics</div>
        <div class="ts-side-note">These drivers appear on bookings but don't match any driver profile in your telematics provider. Add them to telematics or correct the spelling to match the telematics profile.</div>
        <table class="ts-table ts-table-narrow">
          <tr><th>Driver Name</th><th>Errors</th></tr>
          <tr v-for="entry in unmatched_driver_names" :key="entry.driver_name">
            <td>{{ entry.driver_name }}</td>
            <td>{{ entry.occurrences }}</td>
          </tr>
        </table>
      </div>

      <div v-if="unmatched_vehicle_ids.length > 0" class="ts-group">
        <div class="ts-group-header">Vehicle IDs not found in telematics</div>
        <div class="ts-side-note">These vehicle IDs appear on bookings but don't match any vehicle in your telematics provider. Placeholder codes (e.g. TRAINING) should be excluded from actuals, real vehicles should be renamed to match.</div>
        <table class="ts-table ts-table-narrow">
          <tr><th>Vehicle ID</th><th>Errors</th></tr>
          <tr v-for="entry in unmatched_vehicle_ids" :key="entry.vehicle_id">
            <td>{{ entry.vehicle_id }}</td>
            <td>{{ entry.occurrences }}</td>
          </tr>
        </table>
      </div>

      <div class="ts-bottom-row">
        <div v-if="errors_by_booking_type.length > 0" class="ts-bottom-col">
          <div class="ts-group-header">Errors by booking type</div>
          <table class="ts-table ts-table-narrow">
            <tr><th>Booking Type</th><th>Errors</th><th>{{ count_label_title }}</th><th>Error Rate</th></tr>
            <tr v-for="entry in errors_by_booking_type" :key="entry.booking_type">
              <td>{{ entry.booking_type }}</td>
              <td>{{ entry.errors }}</td>
              <td>{{ entry.movements }}</td>
              <td>{{ entry.error_rate }}%</td>
            </tr>
          </table>
        </div>

        <div v-if="errors_by_vehicle_type.length > 0" class="ts-bottom-col">
          <div class="ts-group-header">Errors by vehicle type</div>
          <table class="ts-table ts-table-narrow">
            <tr><th>Vehicle Type</th><th>Errors</th><th>{{ count_label_title }}</th><th>Error Rate</th></tr>
            <tr v-for="entry in errors_by_vehicle_type" :key="entry.vehicle_type">
              <td>{{ entry.vehicle_type }}</td>
              <td>{{ entry.errors }}</td>
              <td>{{ entry.movements }}</td>
              <td>{{ entry.error_rate }}%</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="ts-bottom-row">
        <div v-if="errors_by_day.length > 0" class="ts-bottom-col">
          <div class="ts-group-header">Errors by day</div>
          <table class="ts-table ts-table-narrow">
            <tr><th>Date</th><th>Errors</th><th></th></tr>
            <tr v-for="day in errors_by_day" :key="day.date">
              <td>{{ day.date }}</td>
              <td>{{ day.total }}</td>
              <td class="ts-bar-cell"><div class="ts-bar" :style="{ width: bar_width(day.total) }"></div></td>
            </tr>
          </table>
        </div>

        <div v-if="top_error_vehicles.length > 0" class="ts-bottom-col">
          <div class="ts-group-header">Vehicles with the most errors</div>
          <table class="ts-table ts-table-narrow">
            <tr><th>Vehicle</th><th>Errors</th></tr>
            <tr v-for="entry in top_error_vehicles" :key="entry.vehicle">
              <td>{{ entry.vehicle }}</td>
              <td>{{ entry.errors }}</td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import {get_actuals_error_summary} from "../networking/apis"
import {get_tbn_actuals_error_summary} from "../networking/tbn_apis"
import actuals_troubleshooter_help from '@/components/actuals_troubleshooter_help'

export default {
  name: 'actuals_troubleshooter',

  components: {
    'actuals-troubleshooter-help': actuals_troubleshooter_help,
  },

  data() {
    return {
      loading: false,
      summary: {},
      error_categories: [],
      error_groups: [],
      errors_by_day: [],
      errors_by_booking_type: [],
      errors_by_vehicle_type: [],
      top_error_vehicles: [],
      unmatched_vehicle_ids: [],
      unmatched_driver_names: [],
      expanded: {},
      details_fromDate: justDate(moment().subtract(6, 'days')),
      details_toDate: justDate(moment()),
      actuals_filter: 'finish',
      selected_company: '',
      selected_type: '',
      selected_base: '',
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(180, 'days'),
        maxDate: moment().add(1, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    source() {
      const source = this.$route.query.source
      return ['cm', 'tbn', 'fasttrak'].includes(source) ? source : 'cm'
    },
    count_label() {
      return this.source === 'tbn' ? 'bookings' : 'movements'
    },
    count_label_title() {
      return this.source === 'tbn' ? 'Bookings' : 'Movements'
    },
    back_link() {
      const paths = { cm: '/actuals', tbn: '/actuals_tbn', fasttrak: '/actuals_fasttrak' }
      return { path: paths[this.source] }
    },
    groups_with_categories() {
      const group_labels = (this.summary && this.summary.group_labels) || {}
      const group_stats = {}
      ;(this.error_groups || []).forEach(group => {
        group_stats[group.id] = group
      })
      const order = ['booking_data', 'telematics_matching', 'other']
      return order
        .map(group_id => {
          const categories = this.error_categories.filter(cat => cat.group === group_id)
          const stats = group_stats[group_id] || {}
          return {
            id: group_id,
            label: group_labels[group_id] || group_id,
            categories,
            bookings: stats.bookings || 0,
          }
        })
        .filter(group => group.categories.length > 0)
    },
    max_day_errors() {
      return Math.max(1, ...this.errors_by_day.map(day => day.total))
    },
  },

  async created() {
    if (this.$route.query.fromDate)
      this.details_fromDate = this.$route.query.fromDate
    if (this.$route.query.toDate)
      this.details_toDate = this.$route.query.toDate
    if (this.$route.query.filter)
      this.actuals_filter = this.$route.query.filter
    if (this.$route.query.company)
      this.selected_company = this.$route.query.company
    if (this.$route.query.type)
      this.selected_type = this.$route.query.type
    if (this.$route.query.base)
      this.selected_base = this.$route.query.base
    await this.load_data()
  },

  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      },
    },
  },

  methods: {
    async load_data() {
      if (!this.user || !this.user.tenant)
        return
      this.loading = true
      try {
        const from = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        const to = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')

        const api = this.source === 'tbn' ? get_tbn_actuals_error_summary : get_actuals_error_summary
        const { value: { data = {} }} = this.source === 'tbn'
          ? await api(this.user.tenant, from, to, this.actuals_filter, this.selected_base)
          : await api(this.user.tenant, from, to, this.actuals_filter, this.selected_company, this.selected_type)

        this.summary = data.summary || {}
        this.error_categories = data.error_categories || []
        this.error_groups = data.error_groups || []
        this.errors_by_day = data.errors_by_day || []
        this.errors_by_booking_type = data.errors_by_booking_type || []
        this.errors_by_vehicle_type = data.errors_by_vehicle_type || []
        this.top_error_vehicles = data.top_error_vehicles || []
        this.unmatched_vehicle_ids = data.unmatched_vehicle_ids || []
        this.unmatched_driver_names = data.unmatched_driver_names || []
        this.expanded = {}
        if (this.summary.timezone)
          moment.tz.setDefault(this.summary.timezone)
      } catch (e) {
        this.$emit('showAlert', 'error loading data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    toggle_category(cat_id) {
      this.$set(this.expanded, cat_id, !this.expanded[cat_id])
    },
    async shift_dates(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      await this.load_data()
    },
    booking_label(booking) {
      return booking.booking_id || booking.booking_hash
    },
    booking_link(booking) {
      const paths = { cm: '/actuals', tbn: '/actuals_tbn', fasttrak: '/actuals_fasttrak' }
      return { path: paths[this.source], query: { booking_id: booking.booking_hash || this.booking_label(booking) } }
    },
    bar_width(total) {
      return Math.round(100.0 * total / this.max_day_errors) + '%'
    },
  },
}
</script>

<style scoped>
.ts-summary-bar {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
}
.ts-chip {
  display: inline-block;
  padding: 3px 12px;
  margin-right: 8px;
  border-radius: 12px;
  border: 1px solid #ccc;
}
.ts-chip-total { background-color: #f5f5f5; }
.ts-chip-success { background-color: #d4edda; color: #155724; }
.ts-chip-error { background-color: #f8d7da; color: #721c24; }
.ts-chip-neutral { background-color: #e2e3e5; color: #383d41; }

.ts-group {
  margin-bottom: 30px;
}
.ts-group-header {
  font-size: 20px;
  font-weight: bold;
  border-bottom: 2px solid #888;
  margin-bottom: 10px;
  padding-bottom: 4px;
  text-align: left;
}
.ts-group-count {
  font-size: 15px;
  font-weight: normal;
  font-style: italic;
  margin-left: 12px;
  color: #555;
}

.ts-category-card {
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 12px;
  text-align: left;
}
.ts-category-header {
  background-color: #f0f0f0;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 17px;
}
.ts-category-toggle { margin-right: 8px; }
.ts-category-title { font-weight: bold; }
.ts-category-stats {
  float: right;
  color: #721c24;
  font-weight: bold;
}
.ts-category-body {
  padding: 8px 12px;
  font-size: 15px;
}
.ts-category-explanation { margin-bottom: 4px; }
.ts-category-fix { margin-bottom: 4px; color: #155724; }
.ts-category-bookings { margin-top: 10px; }

.ts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.ts-table th, .ts-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  text-align: left;
}
.ts-table th { background-color: #f5f5f5; }
.ts-table-narrow { max-width: 500px; }
.ts-more-note {
  font-style: italic;
  font-size: 13px;
  color: #555;
  margin-top: 4px;
}
.ts-side-note {
  font-style: italic;
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
  text-align: left;
}

.ts-bottom-row {
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
}
.ts-bottom-row + .ts-bottom-row {
  margin-top: 30px;
}
.ts-bottom-col {
  flex: 1;
  min-width: 300px;
}
.ts-bar-cell { width: 40%; }
.ts-bar {
  background-color: #dc3545;
  height: 12px;
  border-radius: 2px;
}
</style>
